// ===========================================
// Lesson Pieces Card
// ===========================================
// Displays pieces assigned to a class with isCurrent toggle
// Admin can add/remove pieces, Teacher can toggle isCurrent

import { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
  Switch,
  IconButton,
  Skeleton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  MusicNote as MusicNoteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PictureAsPdfIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useLessonPieces, useToggleLessonPieceCurrent, useRemovePieceFromLesson } from '../../../hooks/useGamification';
import { useSnackbar } from 'notistack';
import type { LessonPiece } from '../../../types/gamification.types';
import AssignPieceToLessonModal from './AssignPieceToLessonModal';
import ConfirmDialog from '../../common/ConfirmDialog';

interface LessonPiecesCardProps {
  lessonId: string;
  instrumentId?: string | null;
  programLevelId?: string | null;
  isTeacherView?: boolean;
}

export default function LessonPiecesCard({
  lessonId,
  instrumentId,
  programLevelId,
  isTeacherView = false,
}: LessonPiecesCardProps) {
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<LessonPiece | null>(null);

  const { data: lessonPieces = [], isLoading } = useLessonPieces(lessonId);
  const toggleCurrent = useToggleLessonPieceCurrent();
  const removePiece = useRemovePieceFromLesson();
  const { enqueueSnackbar } = useSnackbar();

  const handleToggleCurrent = async (lessonPiece: LessonPiece) => {
    try {
      await toggleCurrent.mutateAsync({
        lessonPieceId: lessonPiece.id,
        isCurrent: !lessonPiece.isCurrent,
      });
      enqueueSnackbar(
        lessonPiece.isCurrent
          ? `"${lessonPiece.piece?.title}" moved to past pieces`
          : `"${lessonPiece.piece?.title}" marked as currently learning`,
        { variant: 'success' }
      );
    } catch {
      enqueueSnackbar('Failed to update piece status', { variant: 'error' });
    }
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    try {
      await removePiece.mutateAsync({
        lessonId,
        pieceId: confirmRemove.pieceId,
      });
      enqueueSnackbar(`"${confirmRemove.piece?.title}" removed from class`, { variant: 'success' });
      setConfirmRemove(null);
    } catch {
      enqueueSnackbar('Failed to remove piece from class', { variant: 'error' });
    }
  };

  const currentPieces = lessonPieces.filter((lp) => lp.isCurrent);
  const pastPieces = lessonPieces.filter((lp) => !lp.isCurrent);

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MusicNoteIcon color="primary" />
            Class Pieces
          </Typography>
          {!isTeacherView && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setAssignModalOpen(true)}
            >
              Add Piece
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />

        {isLoading ? (
          <Box>
            <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
          </Box>
        ) : lessonPieces.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No pieces assigned to this class yet.
          </Typography>
        ) : (
          <>
            {/* Currently Learning */}
            {currentPieces.length > 0 && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  Currently Learning ({currentPieces.length})
                </Typography>
                <List dense disablePadding>
                  {currentPieces.map((lp) => (
                    <PieceListItem
                      key={lp.id}
                      lessonPiece={lp}
                      isTeacherView={isTeacherView}
                      onToggleCurrent={handleToggleCurrent}
                      onRemove={setConfirmRemove}
                    />
                  ))}
                </List>
              </>
            )}

            {/* Past Pieces */}
            {pastPieces.length > 0 && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mb: 1, mt: currentPieces.length > 0 ? 2 : 0 }}
                >
                  Past Pieces ({pastPieces.length})
                </Typography>
                <List dense disablePadding>
                  {pastPieces.map((lp) => (
                    <PieceListItem
                      key={lp.id}
                      lessonPiece={lp}
                      isTeacherView={isTeacherView}
                      onToggleCurrent={handleToggleCurrent}
                      onRemove={setConfirmRemove}
                    />
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </Paper>

      {/* Assign Piece Modal */}
      <AssignPieceToLessonModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        lessonId={lessonId}
        instrumentId={instrumentId}
        programLevelId={programLevelId}
        existingPieceIds={lessonPieces.map((lp) => lp.pieceId)}
      />

      {/* Remove Confirmation */}
      <ConfirmDialog
        open={!!confirmRemove}
        title="Remove Piece from Class"
        message={`Remove "${confirmRemove?.piece?.title}" from this class? Student progress on this piece will be preserved.`}
        confirmLabel="Remove"
        confirmColor="error"
        onConfirm={handleRemove}
        onCancel={() => setConfirmRemove(null)}
        loading={removePiece.isPending}
      />
    </>
  );
}

// ===========================================
// PIECE LIST ITEM SUB-COMPONENT
// ===========================================

interface PieceListItemProps {
  lessonPiece: LessonPiece;
  isTeacherView: boolean;
  onToggleCurrent: (lp: LessonPiece) => void;
  onRemove: (lp: LessonPiece) => void;
}

function PieceListItem({ lessonPiece, isTeacherView, onToggleCurrent, onRemove }: PieceListItemProps) {
  const piece = lessonPiece.piece;
  if (!piece) return null;

  return (
    <ListItem
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 0.5,
        backgroundColor: lessonPiece.isCurrent ? 'action.hover' : 'transparent',
      }}
    >
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600}>
              {piece.title}
            </Typography>
            {piece.composer && (
              <Typography variant="body2" color="text.secondary">
                by {piece.composer}
              </Typography>
            )}
            {(piece._count?.skills ?? 0) > 0 && (
              <Chip
                label={`${piece._count?.skills} skills`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {piece.lessonPlanUrl && (
              <Box
                component="a"
                href={piece.lessonPlanUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.25,
                  color: 'error.main',
                  textDecoration: 'none',
                  fontSize: '0.7rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 13 }} />
                {piece.lessonPlanName || 'Lesson Plan'}
                <OpenInNewIcon sx={{ fontSize: 11 }} />
              </Box>
            )}
          </Box>
        }
      />
      <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Tooltip title={lessonPiece.isCurrent ? 'Mark as past' : 'Mark as currently learning'}>
          <Switch
            size="small"
            checked={lessonPiece.isCurrent}
            onChange={() => onToggleCurrent(lessonPiece)}
            color="success"
            inputProps={{ 'aria-label': `Toggle ${piece.title} as currently learning` }}
          />
        </Tooltip>
        {!isTeacherView && (
          <Tooltip title="Remove from class">
            <IconButton size="small" onClick={() => onRemove(lessonPiece)} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </ListItemSecondaryAction>
    </ListItem>
  );
}
